// src/components/orders/CreateOrderModal.js — création de commande (admin / marchand)
//
// PARCOURS EN DEUX TEMPS : on choisit d'abord LE MARCHAND, puis on pioche parmi
// SES offres.
//
// Pourquoi : la version precedente chargeait une liste globale plafonnee a 200
// offres dans un menu deroulant unique. Une seule categorie en compte deja plus
// de 100 — au-dela du plafond, des offres etaient absentes du menu SANS AUCUN
// message, et l'agent au guichet concluait que l'offre n'existait pas. Filtrer
// par marchand ramene chaque liste a une taille reelle et supprime le plafond
// silencieux.
//
// Une recherche directe par titre reste disponible : les offres creees par
// l'administration n'ont pas de marchand et ne sortiraient dans aucune fiche.
//
// Maxula — AbodyStudio Limited

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiX, FiTrash2, FiSearch, FiArrowLeft, FiPlus, FiEdit3, FiLoader,
  FiAlertTriangle, FiCheckCircle, FiClock, FiSmartphone
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { orderAPI, dealAPI, merchantAPI } from '../../services/api';
import ShareOrderModal from './ShareOrderModal';
import PayExpressCodeModal from './PayExpressCodeModal';

// Moyens d'encaissement du guichet, dans l'ordre demande.
//
// « En attente » figure dans la MEME liste, comme demande. Ce n'est pas un
// moyen de paiement mais un etat : le choisir n'encaisse pas la commande et ne
// genere donc AUCUN coupon. La consequence a connaitre — quand le client
// reviendra payer, il faudra repasser sur la commande pour indiquer par quoi il
// a regle, sinon la caisse gardera « en attente » comme moyen.
const PENDING = 'pending';
const PAYEXPRESS = 'payexpress';
const PAY_OPTIONS = [
  { v: 'cash', l: 'Espèce' },
  { v: 'tpe', l: 'TPE' },
  { v: 'virement', l: 'Virement' },
  { v: 'payzone', l: 'Payzone' },
  { v: PAYEXPRESS, l: 'PayExpress (le client paiera en agence)' },
  { v: PENDING, l: 'En attente (non payée)' }
];

// Libelle court, pour le rappel d'encaissement.
const methodLabel = (v) => (PAY_OPTIONS.find((o) => o.v === v) || {}).l || v;

const dealPrice = (d) => Number(d?.discountedPrice || d?.price) || 0;
const idOf = (o) => String(o?.id ?? o?._id ?? '');

// `initial` sert a DUPLIQUER une commande : meme client, memes offres, en
// repartant a zero. Absent, le formulaire s'ouvre vide comme avant — c'est
// pourquoi il n'y a rien a changer dans les appels existants.
export default function CreateOrderModal({ scope = 'admin', onClose, onCreated, initial = null }) {
  // Source des offres — 'merchant' : on part d'un marchand.
  //                    'search'   : on cherche un titre partout.
  const [mode, setMode] = useState('merchant');
  const [merchantQuery, setMerchantQuery] = useState('');
  const [merchants, setMerchants] = useState([]);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [merchant, setMerchant] = useState(null);

  const [deals, setDeals] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [dealQuery, setDealQuery] = useState('');

  // Les lignes dupliquees gardent le prix affiche a titre indicatif : le
  // serveur recalcule tout a la creation, donc une offre dont le prix a change
  // depuis la commande d'origine partira au NOUVEAU prix. C'est voulu — on
  // duplique un panier, pas un tarif figé.
  const [lines, setLines] = useState(
    initial && Array.isArray(initial.lines) ? initial.lines : []
  );
  const [customer, setCustomer] = useState(
    initial && initial.customer
      ? { name: initial.customer.name || '', phone: initial.customer.phone || '', email: initial.customer.email || '' }
      : { name: '', phone: '', email: '' }
  );
  // v1.405.0 (client) — ouverture depuis une fiche client : la commande est rattachee a
  // CETTE fiche. Si les coordonnees sont masquees (commercial sans acces au
  // contact), le serveur les relit lui-meme a partir de l'identifiant.
  const ficheId = initial && initial.customerId ? initial.customerId : null;
  const contactMasque = !!(initial && initial.contactMasque);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  // Plus de drapeau independant : l'encaissement decoule du moyen choisi.
  const isPending = paymentMethod === PENDING;
  const isPayexpress = paymentMethod === PAYEXPRESS;
  const [submitting, setSubmitting] = useState(false);
  const [createdOrderNumber, setCreatedOrderNumber] = useState(null);
  // Commande PayExpress creee : on enchaine sur l'ecran du code a transmettre.
  const [payexpressOrder, setPayexpressOrder] = useState(null);
  // RAPPEL D'ENCAISSEMENT : etape obligatoire entre le bouton et l'appel API.
  // Le moyen par defaut est l'espece, et l'oubli d'encaisser ne se voit nulle
  // part ensuite — la commande est enregistree « payee » sans que l'argent soit
  // dans la caisse. Un simple texte d'avertissement ne suffisait pas : il se lit
  // apres coup. Ici, la question bloque et impose de choisir.
  const [askPayment, setAskPayment] = useState(false);
  // REMISE COMMERCIALE (client fidele, geste au comptoir). Pourcentage ou
  // montant fixe. Le calcul definitif est refait par le serveur : ce qui suit
  // n'est qu'un apercu a l'ecran.
  // VENDEUR CREDITE. Vide = la vente compte pour celui qui saisit.
  // Utile quand le client d'un collegue absent se presente au comptoir.
  const [salesRepId, setSalesRepId] = useState('');
  const [staff, setStaff] = useState([]);
  // EXTRA MAXULA : supplement encaisse en plus du deal (chauffeur, transfert).
  // Il n'appartient PAS au partenaire — voir le commentaire du modele Order.
  const [extraAmount, setExtraAmount] = useState('');
  const [extraLabel, setExtraLabel] = useState('');
  const [discountType, setDiscountType] = useState('amount');
  const [discountValue, setDiscountValue] = useState('');

  useEffect(() => {
    // Liste chargee en silence : si le compte n'a pas le droit de la lire, le
    // selecteur ne s'affiche simplement pas. Pas d'erreur a l'ecran pour une
    // fonction facultative.
    let vivant = true;
    (async () => {
      try {
        const r = await orderAPI.staffList();
        const l = r?.data?.data || r?.data || [];
        if (vivant) setStaff(Array.isArray(l) ? l.filter((u) => u && u.id) : []);
      } catch (e) { /* silencieux */ }
    })();
    return () => { vivant = false; };
  }, []);

  const merchantTimer = useRef(null);
  const dealTimer = useRef(null);

  // Espace marchand : pas de choix a faire, ce sont forcement ses offres.
  useEffect(() => {
    if (scope !== 'merchant') return;
    setLoadingDeals(true);
    (async () => {
      try {
        const res = await merchantAPI.getCurrentDeals();
        const arr = res?.data?.data ?? res?.data ?? [];
        setDeals(Array.isArray(arr) ? arr : []);
      } catch (e) {
        setDeals([]);
      } finally {
        setLoadingDeals(false);
      }
    })();
  }, [scope]);

  // ADMIN — recherche de marchands, avec temporisation.
  // Sans elle, chaque frappe declencherait un appel : huit lettres tapees vite,
  // ce sont huit requetes dont sept sont deja perimees en arrivant, et rien ne
  // garantit qu'elles reviennent dans l'ordre — la liste affichee pourrait
  // correspondre a une recherche anterieure.
  useEffect(() => {
    if (scope === 'merchant' || mode !== 'merchant' || merchant) return undefined;
    clearTimeout(merchantTimer.current);
    merchantTimer.current = setTimeout(async () => {
      setLoadingMerchants(true);
      try {
        const res = await merchantAPI.getAll({ search: merchantQuery.trim(), limit: 25 });
        const arr = res?.data?.data ?? res?.data?.merchants ?? res?.data ?? [];
        setMerchants(Array.isArray(arr) ? arr : []);
      } catch (e) {
        setMerchants([]);
      } finally {
        setLoadingMerchants(false);
      }
    }, 350);
    return () => clearTimeout(merchantTimer.current);
  }, [merchantQuery, scope, mode, merchant]);

  // ============================================================
  // OFFRES DU MARCHAND — v1.468.0
  //
  // CORRECTIF : la recherche est confiee au SERVEUR.
  //
  // La version precedente demandait `limit: 200` puis filtrait le titre dans
  // le navigateur. Deux defauts se combinaient :
  //   - le serveur PLAFONNE la limite a 100 (Math.min(100, ...)), le parametre
  //     200 n'a jamais ete respecte ;
  //   - Parapharmacie Tanger compte 737 annonces. On filtrait donc sur les 100
  //     premieres, triees par date, et tout produit plus ancien etait
  //     introuvable — « Aucune offre » alors qu'il existe.
  //
  // Le serveur, lui, cherche dans la totalite. On lui transmet donc la saisie,
  // avec le meme delai de 350 ms que la recherche libre pour ne pas lancer une
  // requete a chaque touche.
  // ============================================================
  useEffect(() => {
    if (!merchant) return undefined;
    clearTimeout(dealTimer.current);
    const q = dealQuery.trim();
    dealTimer.current = setTimeout(async () => {
      setLoadingDeals(true);
      try {
        const params = { merchantId: idOf(merchant), limit: 100 };
        if (q) params.search = q;
        const res = await dealAPI.getAll(params);
        const arr = res?.data?.data ?? res?.data ?? [];
        setDeals(Array.isArray(arr) ? arr : []);
      } catch (e) {
        setDeals([]);
        toast.error('Impossible de charger les offres de ce marchand');
      } finally {
        setLoadingDeals(false);
      }
    }, q ? 350 : 0);
    return () => clearTimeout(dealTimer.current);
  }, [merchant, dealQuery]);

  // Recherche libre par titre — couvre les offres de l'administration, qui
  // n'ont pas de marchand et n'apparaitraient dans aucune fiche.
  useEffect(() => {
    if (mode !== 'search' || scope === 'merchant') return undefined;
    clearTimeout(dealTimer.current);
    if (!dealQuery.trim()) { setDeals([]); return undefined; }
    dealTimer.current = setTimeout(async () => {
      setLoadingDeals(true);
      try {
        const res = await dealAPI.getAll({ search: dealQuery.trim(), limit: 40 });
        const arr = res?.data?.data ?? res?.data ?? [];
        setDeals(Array.isArray(arr) ? arr : []);
      } catch (e) {
        setDeals([]);
      } finally {
        setLoadingDeals(false);
      }
    }, 350);
    return () => clearTimeout(dealTimer.current);
  }, [dealQuery, mode, scope]);

  // v1.468.0 — PLUS DE FILTRAGE LOCAL.
  //
  // C'est le serveur qui filtre desormais, dans la totalite du catalogue. Un
  // second filtre ici retirerait des resultats qu'il vient justement de
  // trouver : le titre peut differer de la saisie (accents, casse, mot present
  // dans la description et non dans le titre).
  const visibleDeals = deals;

  const addDeal = (d) => {
    const id = idOf(d);
    setLines((prev) => {
      const i = prev.findIndex((l) => l.dealId === id);
      // Ajouter deux fois la meme offre incremente la quantite plutot que de
      // creer une seconde ligne : deux lignes identiques sur un bon de commande
      // ressemblent a une erreur de saisie.
      if (i >= 0) {
        return prev.map((l, idx) => (idx === i ? { ...l, quantity: Number(l.quantity || 1) + 1 } : l));
      }
      return [...prev, { dealId: id, title: d.title || '', price: dealPrice(d), quantity: 1, editing: false }];
    });
  };

  const setLine = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const total = useMemo(
    () => lines.reduce((acc, l) => acc + Number(l.price || 0) * (Number(l.quantity) || 0), 0),
    [lines]
  );

  // Apercu de la remise. Bornee au sous-total : au-dela, le total deviendrait
  // negatif et Maxula devrait de l'argent au client.
  const discountAmount = (() => {
    const v = Number(discountValue);
    if (!Number.isFinite(v) || v <= 0) return 0;
    const r = discountType === 'percent' ? total * Math.min(100, v) / 100 : v;
    return Math.min(Math.max(0, Math.round(r * 100) / 100), total);
  })();
  const extraValue = (() => {
    const v = Number(extraAmount);
    return Number.isFinite(v) && v > 0 ? Math.round(v * 100) / 100 : 0;
  })();
  const finalTotal = Math.round((total - discountAmount + extraValue) * 100) / 100;

  // Controles de saisie, executes AVANT le rappel d'encaissement : inutile de
  // demander « avez-vous encaisse ? » sur une commande qui ne partira pas.
  const validate = () => {
    if (!lines.filter((l) => l.dealId).length) { toast.error('Ajoutez au moins une offre'); return false; }
    if (!ficheId && !customer.name.trim() && !customer.email.trim()) {
      toast.error("Renseignez au moins le nom ou l'email du client");
      return false;
    }
    return true;
  };

  const askBeforeCreate = () => { if (validate()) setAskPayment(true); };

  // paid : la commande est-elle encaissee MAINTENANT ?
  //  - true  -> statut payee, coupons generes immediatement
  //  - false -> non payee, aucun coupon tant que l'argent n'est pas entre
  // Le MOYEN reste celui qui a ete choisi, meme quand rien n'a ete encaisse :
  // « Espece, non payee » est une information exploitable, « En attente » sans
  // moyen en est une qu'on aura perdue quand le client reviendra regler.
  const submit = async (paid) => {
    const items = lines
      .filter((l) => l.dealId)
      .map((l) => ({
        dealId: Number(l.dealId),
        quantity: Math.max(1, Number(l.quantity) || 1),
        // Seul le TEXTE est transmis. Le prix reste calcule par le serveur a
        // partir de l'offre : accepter un prix envoye par le navigateur
        // permettrait de commander a 0 MAD.
        titleOverride: l.title || undefined
      }));
    if (!items.length) { toast.error('Ajoutez au moins une offre'); return; }

    setSubmitting(true);
    try {
      const res = await orderAPI.createManual({
        items,
        customer,
        paymentMethod,
        // Seule la REGLE de remise est envoyee (type + valeur), jamais le total.
        // Le serveur recalcule tout : accepter un montant venu de l'ecran
        // permettrait d'enregistrer une vente a n'importe quel prix.
        discount: Number(discountValue) > 0 ? { type: discountType, value: Number(discountValue) } : null,
        extra: extraValue > 0 ? { amount: extraValue, label: extraLabel.trim() } : null,
        // Vide = la vente revient a celui qui saisit. Le serveur revalide que
        // l'identifiant designe bien un compte du personnel actif.
        salesRepId: salesRepId || null,
        customerId: ficheId || undefined,
        markPaid: !!paid
      });
      onCreated && onCreated();
      const order = res?.data?.data?.order || {};
      const num = order.orderNumber;
      const oid = order.id ?? order._id;
      setAskPayment(false);

      // PAYEXPRESS : la commande part non payee et il reste l'essentiel a
      // faire — recuperer le code et le remettre au client. On enchaine donc
      // directement sur cet ecran, sans attendre que l'agent y pense.
      if (isPayexpress && oid) {
        toast.success('Commande créée — génération du code PayExpress');
        setPayexpressOrder({ id: oid, orderNumber: num, amount: finalTotal });
        return;
      }

      toast.success(paid ? 'Commande créée et encaissée' : 'Commande créée — non payée');
      // Admin uniquement : le partage expose les coordonnees du client, que le
      // marchand ne doit pas recevoir (anti-desintermediation).
      if (scope === 'admin' && num) setCreatedOrderNumber(num);
      else onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  if (payexpressOrder) {
    return (
      <PayExpressCodeModal
        orderId={payexpressOrder.id}
        orderNumber={payexpressOrder.orderNumber}
        amount={payexpressOrder.amount}
        customer={customer}
        onClose={onClose}
      />
    );
  }

  if (createdOrderNumber) {
    return <ShareOrderModal orderNumber={createdOrderNumber} onClose={onClose} />;
  }

  const showMerchantPicker = scope === 'admin' && mode === 'merchant' && !merchant;
  const showDealPicker = scope === 'merchant' || merchant || mode === 'search';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b shrink-0">
          <h2 className="text-lg font-bold">{ficheId ? `Bon de commande — ${customer.name || 'client'}` : 'Créer une commande'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Fermer">
            <FiX size={22} />
          </button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto flex-1 min-h-0">
          {/* 1. SOURCE DES OFFRES */}
          {scope === 'admin' && (
            <div>
              <div className="flex items-center justify-between mb-2 gap-2">
                <div className="text-sm font-semibold truncate">
                  {merchant ? `Offres de ${merchant.businessName || 'ce marchand'}` : 'Marchand'}
                </div>
                {merchant ? (
                  <button
                    onClick={() => { setMerchant(null); setDeals([]); setDealQuery(''); }}
                    className="shrink-0 inline-flex items-center gap-1 text-sm text-[#e11d7b] hover:underline"
                  >
                    <FiArrowLeft size={14} /> Changer de marchand
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setMode((m) => (m === 'merchant' ? 'search' : 'merchant'));
                      setDeals([]); setDealQuery('');
                    }}
                    className="shrink-0 text-sm text-[#e11d7b] hover:underline"
                  >
                    {mode === 'merchant' ? "Chercher une offre par son titre" : 'Chercher par marchand'}
                  </button>
                )}
              </div>

              {showMerchantPicker ? (
                <>
                  <div className="relative">
                    <FiSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      autoFocus
                      value={merchantQuery}
                      onChange={(e) => setMerchantQuery(e.target.value)}
                      placeholder="Nom du marchand…"
                      className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
                    />
                  </div>
                  <div className="mt-2 border rounded-lg divide-y max-h-56 overflow-y-auto">
                    {loadingMerchants ? (
                      <div className="p-3 text-sm text-gray-400 flex items-center gap-2">
                        <FiLoader className="animate-spin" size={14} /> Recherche…
                      </div>
                    ) : merchants.length === 0 ? (
                      <div className="p-3 text-sm text-gray-400">Aucun marchand trouvé</div>
                    ) : (
                      merchants.map((m) => (
                        <button
                          key={idOf(m)}
                          type="button"
                          onClick={() => setMerchant(m)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50"
                        >
                          <div className="text-sm font-semibold text-gray-800">{m.businessName || '—'}</div>
                          <div className="text-xs text-gray-500">
                            {[m.city, m.phone].filter(Boolean).join(' · ') || '—'}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </>
              ) : null}
            </div>
          )}

          {/* 2. CHOIX DES OFFRES */}
          {showDealPicker && (
            <div>
              <div className="relative mb-2">
                <FiSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={dealQuery}
                  onChange={(e) => setDealQuery(e.target.value)}
                  placeholder={mode === 'search' && !merchant ? "Titre de l'offre…" : 'Filtrer les offres…'}
                  className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
                />
              </div>

              <div className="border rounded-lg divide-y max-h-56 overflow-y-auto">
                {loadingDeals ? (
                  <div className="p-3 text-sm text-gray-400 flex items-center gap-2">
                    <FiLoader className="animate-spin" size={14} /> Chargement…
                  </div>
                ) : visibleDeals.length === 0 ? (
                  <div className="p-3 text-sm text-gray-400">
                    {mode === 'search' && !merchant && !dealQuery.trim()
                      ? 'Saisissez un titre pour chercher'
                      : 'Aucune offre'}
                  </div>
                ) : (
                  visibleDeals.map((d) => (
                    <button
                      key={idOf(d)}
                      type="button"
                      onClick={() => addDeal(d)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-gray-800 truncate">{d.title}</div>
                        <div className="text-xs text-gray-400 truncate">
                          {d.merchantName || d.merchant?.businessName || ''}
                        </div>
                      </div>
                      <span className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-[#e11d7b]">
                        {dealPrice(d).toFixed(0)} MAD <FiPlus size={14} />
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* 3. PANIER */}
          <div>
            <div className="text-sm font-semibold mb-2">Articles ({lines.length})</div>
            {lines.length === 0 ? (
              <p className="text-sm text-gray-400">
                Aucun article — cliquez une offre ci-dessus pour l'ajouter.
              </p>
            ) : (
              <div className="space-y-2">
                {lines.map((l, i) => (
                  <div key={l.dealId} className="border rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      {l.editing ? (
                        <input
                          autoFocus
                          value={l.title}
                          onChange={(e) => setLine(i, { title: e.target.value })}
                          onBlur={() => setLine(i, { editing: false })}
                          className="flex-1 border rounded px-2 py-1 text-sm"
                        />
                      ) : (
                        <span className="flex-1 text-sm text-gray-800 truncate">{l.title}</span>
                      )}
                      <button
                        type="button"
                        onClick={() => setLine(i, { editing: !l.editing })}
                        className="text-gray-400 hover:text-[#e11d7b]"
                        title="Modifier le libellé imprimé"
                      >
                        <FiEdit3 size={15} />
                      </button>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={l.quantity}
                        onChange={(e) => setLine(i, { quantity: e.target.value })}
                        className="w-16 border rounded px-2 py-1 text-sm"
                      />
                      <span className="w-24 text-right text-sm font-medium text-gray-700">
                        {(Number(l.price) * (Number(l.quantity) || 0)).toFixed(2)} MAD
                      </span>
                      <button onClick={() => removeLine(i)} className="text-gray-400 hover:text-red-600">
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
                <p className="text-xs text-gray-400">
                  Le crayon modifie le libellé imprimé sur le bon de commande et le coupon.
                  Le prix, lui, reste celui de l'offre.
                </p>
              </div>
            )}
          </div>

          {/* 4. CLIENT */}
          <div>
            <div className="text-sm font-semibold mb-2">Client</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} placeholder="Nom complet" className="border rounded-lg px-3 py-2" />
              {contactMasque ? (
                <>
                  <input value={(initial && initial.phoneAffiche) || '•• •• •• ••'} disabled className="border rounded-lg px-3 py-2 bg-gray-50 text-gray-500" />
                  <input value={(initial && initial.emailAffiche) || ''} placeholder="Email (fiche)" disabled className="border rounded-lg px-3 py-2 bg-gray-50 text-gray-500" />
                </>
              ) : (
                <>
                  <input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} placeholder="Téléphone" className="border rounded-lg px-3 py-2" />
                  <input value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} placeholder="Email (optionnel)" className="border rounded-lg px-3 py-2" />
                </>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {ficheId
                ? 'Commande rattachée à la fiche client et inscrite dans son historique de suivi.'
                : "Si l'email correspond à un compte client, la commande lui sera reliée. Sinon, commande « invité »."}
            </p>
          </div>

          {/* 5 ter. VENDEUR CREDITE.
              Le client d'un collegue absent se presente : on saisit pour lui,
              mais la vente doit lui revenir. On garde DEUX informations
              distinctes — qui a tape (responsabilite d'une erreur de saisie)
              et a qui la vente compte (commissionnement). */}
          {/* Affiche des qu'un autre compte existe. Le seuil precedent (> 1)
              masquait le menu quand la liste revenait vide faute de droit —
              ce qui arrivait a TOUS les commerciaux. */}
          {staff.length > 0 && (
            <div className="rounded-lg border border-gray-200 p-3">
              <div className="text-sm font-semibold mb-2">Vente au nom de</div>
              <select
                value={salesRepId}
                onChange={(e) => setSalesRepId(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Moi (par défaut)</option>
                {staff.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Si vous saisissez pour un collègue absent, choisissez-le : la
                vente sera comptée pour lui. Votre nom reste enregistré comme
                auteur de la saisie.
              </p>
            </div>
          )}

          {/* 5 quater. EXTRA MAXULA.
              Encaisse en plus du deal et conserve entierement par Maxula. Le
              partenaire ne le voit nulle part : ni dans son portefeuille, ni
              sur le coupon, ni dans son decompte. */}
          <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3">
            <div className="text-sm font-semibold mb-2">Extra Maxula (optionnel)</div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="number" min="0" step="0.01"
                value={extraAmount}
                onChange={(e) => setExtraAmount(e.target.value)}
                placeholder="Ex : 100"
                className="w-28 border rounded-lg px-3 py-2 text-sm"
              />
              <span className="text-sm font-semibold text-gray-600">MAD</span>
              <input
                value={extraLabel}
                onChange={(e) => setExtraLabel(e.target.value)}
                placeholder="Description : chauffeur, transfert…"
                className="flex-1 min-w-[180px] border rounded-lg px-3 py-2 text-sm"
              />
            </div>
            {extraValue > 0 && (
              <p className="text-sm text-indigo-800 font-semibold mt-2">
                + {extraValue.toFixed(2)} MAD encaissés en plus → total {finalTotal.toFixed(2)} MAD
              </p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Montant conservé entièrement par Maxula. Le partenaire ne le voit
              pas et n'en touche rien — il est payé sur le deal uniquement.
            </p>
          </div>

          {/* 5 bis. REMISE COMMERCIALE.
              Placee AVANT le paiement : le montant a encaisser doit deja tenir
              compte de la remise quand l'agent choisit son moyen de paiement. */}
          <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-sm font-semibold mb-2">Remise (optionnel)</div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setDiscountType('amount')}
                  className={`px-3 py-2 text-sm font-semibold ${discountType === 'amount' ? 'bg-[#e11d7b] text-white' : 'bg-white text-gray-700'}`}
                >
                  MAD
                </button>
                <button
                  type="button"
                  onClick={() => setDiscountType('percent')}
                  className={`px-3 py-2 text-sm font-semibold ${discountType === 'percent' ? 'bg-[#e11d7b] text-white' : 'bg-white text-gray-700'}`}
                >
                  %
                </button>
              </div>
              <input
                type="number" min="0" step="0.01"
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                placeholder={discountType === 'percent' ? 'Ex : 10' : 'Ex : 50'}
                className="w-32 border rounded-lg px-3 py-2 text-sm"
              />
              {discountAmount > 0 && (
                <span className="text-sm text-green-700 font-semibold">
                  −{discountAmount.toFixed(2)} MAD → {finalTotal.toFixed(2)} MAD
                </span>
              )}
              {discountValue !== '' && discountAmount === 0 && (
                <span className="text-sm text-gray-500">Aucune remise appliquée</span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Geste commercial pris sur la marge Maxula. Le partenaire est payé
              sur le prix d'origine — il n'a pas consenti cette remise.
            </p>
          </div>

          {/* 5. PAIEMENT */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <div>
              <div className="text-sm font-semibold mb-1">Méthode de paiement</div>
              <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
                {PAY_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            {/* Ce que produit le moyen choisi, dit AVANT de cliquer. Le rappel
                d'encaissement, lui, arrive juste apres le bouton. */}
            <div className="text-xs text-gray-500 self-end pb-2">
              {isPending ? (
                <span className="text-amber-700">
                  Commande enregistrée <strong>non payée</strong> — aucun coupon ne sera
                  généré. Encaissez-la depuis la liste des commandes pour les créer.
                </span>
              ) : isPayexpress ? (
                <span className="text-amber-700">
                  Le client repart avec un <strong>code à payer en agence</strong>.
                  La commande reste non payée jusqu'au règlement.
                </span>
              ) : (
                <span>
                  Vous encaissez <strong>maintenant</strong> — les coupons sont générés
                  immédiatement.
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Pied fixe : seul acces au bouton de creation, il ne doit ni defiler
            avec le contenu ni se laisser comprimer. */}
        <div className="flex items-center justify-between border-t p-5 shrink-0 bg-gray-50">
          <div className="text-lg font-bold">
            {(discountAmount > 0 || extraValue > 0) && (
              <span className="block text-xs font-normal text-gray-500">
                Deal {total.toFixed(2)}
                {discountAmount > 0 ? ` — remise ${discountAmount.toFixed(2)}` : ''}
                {extraValue > 0 ? ` + extra ${extraValue.toFixed(2)}` : ''}
              </span>
            )}
            Total : <span className="text-[#e11d7b]">{finalTotal.toFixed(2)} MAD</span>
          </div>
          <button
            onClick={askBeforeCreate}
            disabled={submitting}
            className="px-5 py-2.5 rounded-lg bg-[#e11d7b] text-white font-semibold hover:opacity-90 disabled:opacity-60"
          >
            {submitting ? 'Création…' : 'Créer la commande'}
          </button>
        </div>
      </div>

      {/* =====================================================================
          RAPPEL D'ENCAISSEMENT
          Le moyen par defaut est l'espece : la commande part « payee » sans
          qu'aucun ecran n'ait jamais demande si l'argent avait ete pris. Cet
          ecran pose la question une fois, au bon moment, et laisse les deux
          reponses a portee de clic — sinon l'agent presse choisit toujours
          celle qui ferme la fenetre.
          Il se superpose au formulaire (z-60) : la commande est encore
          modifiable derriere, rien n'est envoye tant qu'un bouton n'est pas
          clique.
         ===================================================================== */}
      {askPayment && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
          onClick={() => !submitting && setAskPayment(false)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b flex items-center gap-2">
              <FiAlertTriangle className="text-amber-500" size={20} />
              <h3 className="font-bold">Encaissement</h3>
            </div>

            <div className="p-5 space-y-4">
              <div className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-4 text-center">
                <div className="text-xs uppercase tracking-wide text-gray-500">
                  Montant à percevoir
                </div>
                <div className="text-3xl font-extrabold text-[#e11d7b] my-1">
                  {finalTotal.toFixed(2)} MAD
                </div>
                <div className="text-sm text-gray-600">
                  Moyen choisi : <strong>{methodLabel(paymentMethod)}</strong>
                </div>
              </div>

              {isPayexpress ? (
                <>
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
                    <FiSmartphone className="mt-0.5 shrink-0" size={16} />
                    <div>
                      <strong>Rien à encaisser maintenant.</strong> La commande est
                      créée non payée, un code PayExpress est généré, et vous
                      l'envoyez au client par WhatsApp ou par email. Il paie en
                      agence ; la commande passe alors en payée toute seule et les
                      coupons sont émis.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => submit(false)}
                    disabled={submitting}
                    className="w-full px-5 py-3 rounded-lg bg-[#e11d7b] text-white font-bold hover:opacity-90 disabled:opacity-60"
                  >
                    {submitting ? 'Création…' : 'Créer et générer le code PayExpress'}
                  </button>
                </>
              ) : isPending ? (
                <>
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
                    <FiClock className="mt-0.5 shrink-0" size={16} />
                    <div>
                      La commande sera enregistrée <strong>non payée</strong> et
                      <strong> aucun coupon</strong> ne sera émis. Le client ne repart
                      avec rien : pensez à l'encaisser depuis la liste des commandes
                      quand il réglera.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => submit(false)}
                    disabled={submitting}
                    className="w-full px-5 py-3 rounded-lg bg-amber-600 text-white font-bold hover:opacity-90 disabled:opacity-60"
                  >
                    {submitting ? 'Création…' : 'Créer la commande non payée'}
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-700">
                    Avez-vous bien perçu ce montant&nbsp;? Répondre « oui » enregistre
                    la commande comme <strong>payée</strong> et génère les coupons
                    immédiatement.
                  </p>
                  <button
                    type="button"
                    onClick={() => submit(true)}
                    disabled={submitting}
                    className="w-full px-5 py-3 rounded-lg bg-[#e11d7b] text-white font-bold hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    <FiCheckCircle size={17} />
                    {submitting ? 'Création…' : `Oui, j'ai encaissé ${finalTotal.toFixed(2)} MAD`}
                  </button>
                  <button
                    type="button"
                    onClick={() => submit(false)}
                    disabled={submitting}
                    className="w-full px-5 py-2.5 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 font-semibold hover:bg-amber-100 disabled:opacity-60"
                  >
                    Non — créer la commande non payée
                  </button>
                  <p className="text-xs text-gray-500">
                    « Non » garde le moyen <strong>{methodLabel(paymentMethod)}</strong>
                    {' '}et laisse la commande en attente de règlement : aucun coupon
                    n'est émis tant qu'elle n'est pas encaissée.
                  </p>
                </>
              )}

              <button
                type="button"
                onClick={() => setAskPayment(false)}
                disabled={submitting}
                className="w-full px-5 py-2 text-sm text-gray-500 hover:text-gray-800 disabled:opacity-60"
              >
                Revenir à la commande
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
